/**
 * Notify — confirm + unsubscribe capability-URL landing pages (notify/spec.md §2.2/§3.2). Both
 * pages share this component: a token in the URL does the work, no form, the result renders on
 * load (or after one API call) into one of a small set of states. Same anti-enumeration discipline
 * as the Requests tracking code (ADR-0005) — expired/invalid never differs in wording from
 * "doesn't exist" (never leaks whether an email is on the list).
 *
 * Confirm executes on load. Unsubscribe ALSO executes on load with no confirmation click required
 * (spec §3.1 — true one-click, CAN-SPAM "no additional information required"; do not add an "are
 * you sure" interstitial here).
 *
 * Focus moves to the state heading on resolution; the result is also announced via
 * aria-live="polite" for the client-rendered case (spec §2.3/§3.3).
 *
 * Expired-token confirm state (spec §2.2 table) offers an inline email field + "Resend
 * confirmation" button rather than just a link to /notify/subscribe — same resend flow as
 * SubscribePage's post-submit state (§1.6), same anti-enumeration posture (always a success-shaped
 * response, never reveals whether the email is on the list).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { confirmSubscription, unsubscribe, resendConfirmation, ApiError } from "../../api/index.js";
import type { NotifyLandingResult } from "../../api/types.js";
import { Field } from "../../components/Field.js";
import { Button } from "../../components/Button.js";
import { CheckIcon, AlertIcon } from "../../components/icons.js";
import { TownHeader } from "../../components/TownHeader.js";
import { ResidentThemeRoot } from "../../components/ResidentThemeRoot.js";
import { useTheme } from "../../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../../components/useDocumentTitle.js";
import { useEmbedClose } from "../../components/useEmbedClose.js";

type Kind = "confirm" | "unsubscribe";
type Resolved = NotifyLandingResult["state"] | "error";

interface Copy {
  headline: string;
  body: (townName: string) => string;
  icon: "check" | "alert";
}

const CONFIRM_COPY: Record<Resolved, Copy> = {
  success: {
    headline: "You're confirmed!",
    body: (t) =>
      `You'll now get city updates from ${t} by email. You can unsubscribe anytime — every email has a link.`,
    icon: "check",
  },
  already: {
    headline: "You're already confirmed.",
    body: () => "No action needed — you're on the list.",
    icon: "check",
  },
  expired: {
    headline: "That link has expired.",
    body: () => "Confirmation links are valid for 7 days. Enter your email again to get a new one.",
    icon: "alert",
  },
  "unsubscribed-since": {
    headline: "This link is no longer active.",
    body: () => "It looks like you unsubscribed after signing up. Want to subscribe again?",
    icon: "alert",
  },
  error: {
    headline: "That link didn't work.",
    body: () => "Something went wrong. Please try again in a moment.",
    icon: "alert",
  },
};

const UNSUB_COPY: Record<Resolved, Copy> = {
  success: {
    headline: "You're unsubscribed.",
    body: (t) =>
      `You won't get any more city update emails from ${t}. If that was a mistake, you can subscribe again anytime.`,
    icon: "check",
  },
  already: {
    headline: "You're already unsubscribed.",
    body: () => "No action needed — you're not receiving city update emails.",
    icon: "check",
  },
  expired: {
    headline: "That link didn't work.",
    body: () =>
      "If you're still getting emails you don't want, subscribe again and then unsubscribe, or reply to any update and we'll remove you by hand.",
    icon: "alert",
  },
  "unsubscribed-since": {
    headline: "That link didn't work.",
    body: () =>
      "If you're still getting emails you don't want, subscribe again and then unsubscribe, or reply to any update and we'll remove you by hand.",
    icon: "alert",
  },
  error: {
    headline: "That link didn't work.",
    body: () => "Something went wrong. Please try again in a moment.",
    icon: "alert",
  },
};

interface Props {
  kind: Kind;
  embed?: boolean;
}

type ResendState = "idle" | "sending" | "sent" | "rate-limited" | "error";

export function NotifyLandingPage({ kind, embed = false }: Props): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { config } = useTheme();
  useDocumentTitle(
    pageTitle(kind === "confirm" ? "Confirm Subscription" : "Unsubscribe", config.townName),
  );
  useEmbedClose(embed);
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Expired-confirm inline resend (spec §2.2) — same anti-enumeration posture as the subscribe
  // form's resend (§1.6): the response is always success-shaped, never reveals list membership.
  const [resendEmail, setResendEmail] = useState("");
  const [resendEmailError, setResendEmailError] = useState<string | null>(null);
  const [resendState, setResendState] = useState<ResendState>("idle");

  useEffect(() => {
    let live = true;
    const run = kind === "confirm" ? confirmSubscription : unsubscribe;
    if (!token) {
      setResolved("error");
      return;
    }
    run(token)
      .then((res) => {
        if (live) setResolved(res.state);
      })
      .catch((err) => {
        if (!live) return;
        // Anti-enumeration: even a network/validation failure reads as the generic
        // expired/invalid copy, never a distinct "not found" state.
        setResolved(err instanceof ApiError ? "expired" : "error");
      });
    return () => {
      live = false;
    };
  }, [kind, token]);

  useEffect(() => {
    if (resolved) headingRef.current?.focus();
  }, [resolved]);

  const copyMap = kind === "confirm" ? CONFIRM_COPY : UNSUB_COPY;
  const copy = resolved ? copyMap[resolved] : null;

  const onResendSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resendState === "sending") return;
    const trimmed = resendEmail.trim();
    if (!isValidEmail(trimmed)) {
      setResendEmailError("Enter a valid email address, like name@example.com.");
      return;
    }
    setResendEmailError(null);
    setResendState("sending");
    try {
      await resendConfirmation(trimmed);
      setResendState("sent");
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setResendState("rate-limited");
      else setResendState("error");
    }
  };

  const showInlineResend = kind === "confirm" && resolved === "expired";

  return (
    <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
      <TownHeader embed={embed} />
      <main className="td-confirm">
        {!resolved ? (
          <p role="status" className="td-track__loading">
            {kind === "confirm" ? "Confirming…" : "Unsubscribing…"}
          </p>
        ) : (
          <>
            <p className="td-sr-only" aria-live="polite">
              {copy!.headline} {copy!.body(config.townName)}
            </p>
            <div className="td-confirm__mark" aria-hidden="true">
              {copy!.icon === "check" ? (
                <CheckIcon width={28} height={28} />
              ) : (
                <AlertIcon width={28} height={28} />
              )}
            </div>
            <h1 className="td-confirm__title" tabIndex={-1} ref={headingRef}>
              {copy!.headline}
            </h1>
            <p className="td-confirm__sub">{copy!.body(config.townName)}</p>

            {showInlineResend ? (
              resendState === "sent" ? (
                <p aria-live="polite" className="td-submit__hint">
                  Sent — check your inbox.
                </p>
              ) : (
                <form
                  className="td-confirm__resend"
                  noValidate
                  onSubmit={onResendSubmit}
                  aria-busy={resendState === "sending"}
                >
                  <Field
                    id="field-resend-email"
                    label="Email"
                    required
                    error={resendEmailError ?? undefined}
                  >
                    {(aria) => (
                      <input
                        {...aria}
                        className="td-input"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={resendEmail}
                        onChange={(e) => {
                          setResendEmail(e.target.value);
                          if (resendEmailError) setResendEmailError(null);
                        }}
                      />
                    )}
                  </Field>
                  <div className="td-confirm__actions">
                    <Button type="submit" disabled={resendState === "sending"}>
                      {resendState === "sending" ? "Sending…" : "Resend confirmation"}
                    </Button>
                  </div>
                  <p aria-live="polite" className="td-submit__hint">
                    {resendState === "rate-limited"
                      ? "Please wait a minute before requesting another."
                      : null}
                    {resendState === "error" ? "We couldn't send that — try again." : null}
                  </p>
                </form>
              )
            ) : null}

            <div className="td-confirm__actions">
              {resolved === "unsubscribed-since" ||
              (!showInlineResend && resolved === "expired") ? (
                <Link to="/notify/subscribe" className="td-btn td-btn--primary">
                  Subscribe again
                </Link>
              ) : null}
              {config.websiteUrl ? (
                <a
                  href={config.websiteUrl}
                  rel="noopener noreferrer"
                  className="td-btn td-btn--secondary"
                >
                  Back to {config.townName}'s website
                </a>
              ) : null}
            </div>
          </>
        )}
      </main>
      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
      </footer>
    </ResidentThemeRoot>
  );
}

function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}
